import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Images, attributeTypes } from 'utils';
import { SelectTypeAutocomplete } from 'components';
import {
    conditionRuleTypes,
    conditionRuleTypeOptions,
    conditionOperators,
    conditionOperatorOptions,
    valuelessOperators,
    multiValueOperators,
} from './conditionConstants';
import addBluePlus from '../../../../../assets/images/icons/addBluePlus.svg';

/** Attribute types whose properties.options can be referenced by conditions */
const OPTION_BASED_TYPES = [attributeTypes.SELECT, attributeTypes.CHECK_BOX, attributeTypes.RADIO_BUTTON];

/**
 * Build the list of fields available as condition sources.
 * All section attributes can be connected — excludes only the current field itself.
 */
const getAvailableFields = (allAttributes, currentFieldId) => {
    return (allAttributes || []).filter((attr) => attr?.id !== currentFieldId && attr?.id);
};

export const ConditionsDrawer = ({ selectedItem, handleUpdate, allAttributes, onBack }) => {
    const rules = selectedItem?.properties?.conditions || [];

    const updateRules = (newRules) => {
        handleUpdate('conditions', newRules);
    };

    // ── Rule CRUD ──

    const addRule = () => {
        const newRule = {
            id: uuidv4(),
            type: conditionRuleTypes.SHOW_IF,
            conditions: [],
        };
        updateRules([...rules, newRule]);
    };

    const removeRule = (ruleId) => {
        updateRules(rules.filter((r) => r.id !== ruleId));
    };

    const updateRuleType = (ruleId, newType) => {
        updateRules(rules.map((r) => (r.id === ruleId ? { ...r, type: newType } : r)));
    };

    // ── Condition CRUD (within a rule) ──

    const addCondition = (ruleId) => {
        const newCondition = {
            id: uuidv4(),
            fieldId: '',
            operator: conditionOperators.IS,
            value: '',
        };
        updateRules(
            rules.map((r) =>
                r.id === ruleId ? { ...r, conditions: [...(r.conditions || []), newCondition] } : r,
            ),
        );
    };

    const removeCondition = (ruleId, conditionId) => {
        updateRules(
            rules.map((r) =>
                r.id === ruleId
                    ? { ...r, conditions: (r.conditions || []).filter((c) => c.id !== conditionId) }
                    : r,
            ),
        );
    };

    const updateCondition = (ruleId, conditionId, field, value) => {
        updateRules(
            rules.map((r) => {
                if (r.id !== ruleId) return r;
                return {
                    ...r,
                    conditions: (r.conditions || []).map((c) => {
                        if (c.id !== conditionId) return c;
                        const updated = { ...c, [field]: value };
                        // Reset value when changing field or operator
                        if (field === 'fieldId') {
                            updated.value = '';
                        }
                        if (field === 'operator' && valuelessOperators.includes(value)) {
                            updated.value = '';
                        }
                        if (field === 'operator' && multiValueOperators.includes(value) && !Array.isArray(updated.value)) {
                            updated.value = [];
                        }
                        if (field === 'operator' && !multiValueOperators.includes(value) && Array.isArray(updated.value)) {
                            updated.value = '';
                        }
                        return updated;
                    }),
                };
            }),
        );
    };

    const availableFields = getAvailableFields(allAttributes, selectedItem?.id);

    /** Map available fields to the { id, name } shape SelectTypeAutocomplete expects */
    const fieldSelectList = availableFields.map((f) => ({
        id: f.id,
        name: f.label || f.name || 'Untitled',
    }));

    const renderValueInput = (condition, ruleId) => {
        if (valuelessOperators.includes(condition.operator)) return null;

        const sourceField = allAttributes?.find((a) => a?.id === condition.fieldId);
        if (!condition.fieldId) return null;

        const isOptionBased = sourceField && OPTION_BASED_TYPES.includes(sourceField.type);
        const options = sourceField?.properties?.options || [];

        if (isOptionBased && options.length > 0) {
            if (multiValueOperators.includes(condition.operator)) {
                // Multi-select (toggle switches)
                const selectedValues = Array.isArray(condition.value) ? condition.value : [];
                return (
                    <div className="condition-value-multi-select">
                        {options.map((opt) => {
                            const optName = opt?.name || opt?.value || '';
                            const isChecked = selectedValues.includes(optName);
                            return (
                                <div key={opt.id} className="condition-value-option">
                                    <span className="condition-value-option-name">{optName}</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                const next = isChecked
                                                    ? selectedValues.filter((v) => v !== optName)
                                                    : [...selectedValues, optName];
                                                updateCondition(ruleId, condition.id, 'value', next);
                                            }}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Single select dropdown
            const valueList = options.map((opt) => ({
                id: opt?.name || opt?.value || '',
                name: opt?.name || opt?.value || '',
            }));
            return (
                <SelectTypeAutocomplete
                    disableClearable={true}
                    name={`condition-value-${condition.id}`}
                    handleSelect={(e) => updateCondition(ruleId, condition.id, 'value', e.target.value)}
                    defaultValue={condition.value || ''}
                    list={valueList}
                    renderValue={(i) => i?.name}
                    uiType={'tableFilter'}
                    noError={true}
                    placeholder="Choose value..."
                />
            );
        }

        // Free text input (for INPUT type fields)
        return (
            <input
                className="condition-text-input"
                type="text"
                placeholder="Enter value..."
                value={condition.value || ''}
                onChange={(e) => updateCondition(ruleId, condition.id, 'value', e.target.value)}
            />
        );
    };

    const renderConditionRow = (condition, ruleId) => {
        return (
            <div key={condition.id} className="condition-row">
                <div className="condition-row-inputs">
                    {/* Top row: Field selector + Operator on the same line */}
                    <div className="condition-row-top">
                        <div className="condition-field-select">
                            <SelectTypeAutocomplete
                                disableClearable={true}
                                name={`condition-field-${condition.id}`}
                                handleSelect={(e) => updateCondition(ruleId, condition.id, 'fieldId', e.target.value)}
                                defaultValue={condition.fieldId || ''}
                                list={fieldSelectList}
                                renderValue={(i) => i?.name}
                                uiType={'tableFilter'}
                                noError={true}
                                placeholder="Select Field"
                            />
                        </div>
                        <div className="condition-operator-select">
                            <SelectTypeAutocomplete
                                disableClearable={true}
                                name={`condition-operator-${condition.id}`}
                                handleSelect={(e) => updateCondition(ruleId, condition.id, 'operator', e.target.value)}
                                defaultValue={condition.operator || ''}
                                list={conditionOperatorOptions}
                                renderValue={(i) => i?.name}
                                uiType={'tableFilter'}
                                noError={true}
                                placeholder="is"
                            />
                        </div>
                    </div>

                    {/* Value row below */}
                    {renderValueInput(condition, ruleId)}
                </div>

                {/* Remove condition button */}
                <button
                    className="condition-remove-btn reset-btn-by-default"
                    type="button"
                    onClick={() => removeCondition(ruleId, condition.id)}
                    title="Remove condition">
                    <img src={Images.closeSingleVector} alt="remove" />
                </button>
            </div>
        );
    };

    const renderRule = (rule, ruleIndex) => {
        return (
            <div key={rule.id} className="condition-rule-card">
                <div className="flex-end">
                    <button
                        className="condition-rule-remove-btn reset-btn-by-default"
                        type="button"
                        onClick={() => removeRule(rule.id)}
                        title="Delete rule">
                        <img src={Images.trash} alt="delete" />
                    </button>
                </div>
                <div className="condition-rule-header">
                    {/* Rule type selector — full width */}
                    <div className="condition-rule-type-select">
                        <SelectTypeAutocomplete
                            disableClearable={true}
                            name={`rule-type-${rule.id}`}
                            handleSelect={(e) => updateRuleType(rule.id, e.target.value)}
                            defaultValue={rule.type}
                            list={conditionRuleTypeOptions}
                            renderValue={(i) => i?.name}
                            uiType={'tableFilter'}
                            noError={true}
                        />
                    </div>

                    {/* Remove rule button */}
                </div>

                {/* Conditions list */}
                <div className="condition-rule-conditions">{(rule.conditions || []).map((cond) => renderConditionRow(cond, rule.id))}</div>

                {/* Add condition within this rule */}
                <button className="condition-add-btn reset-btn-by-default" type="button" onClick={() => addCondition(rule.id)}>
                    <img src={Images.addBluePlus} alt="addBluePlus" />
                    <p> Add Condition</p>
                </button>
            </div>
        );
    };

    const hasRules = rules.length > 0;

    return (
        <div className="conditions-drawer">
            <div className="conditions-drawer-header">
                {/*<button className="conditions-drawer-back reset-btn-by-default" type="button" onClick={onBack}>*/}
                {/*    <img src={Images.backArrow} alt="back" />*/}
                {/*</button>*/}
                <div className="conditions-drawer-title-box">
                    <h3 className="conditions-drawer-title">Conditions</h3>
                    <p className="conditions-drawer-subtitle">for {selectedItem?.label || 'Field'}</p>
                </div>
                <button className="conditions-drawer-close reset-btn-by-default" type="button" onClick={onBack}>
                    <img src={Images.closeSingleVector} alt="close" style={{ width: 18, height: 18 }} />
                </button>
            </div>

            <div className="conditions-drawer-body">
                {!hasRules ? (
                    <div className="conditions-empty-state">
                        <div className="conditions-empty-icon">⚡</div>
                        <p className="conditions-empty-title">No conditions yet</p>
                        <p className="conditions-empty-desc">
                            Make this field smarter by adding rules - show it only when needed, mark it required based on answers, and more.
                        </p>
                        <button className="conditions-empty-add-btn" type="button" onClick={addRule}>
                            <img
                                style={{
                                    filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(2%) hue-rotate(61deg) brightness(106%) contrast(101%)',
                                }}
                                src={Images.plus}
                                alt="add"
                            />
                            <p> Add Condition</p>
                        </button>
                    </div>
                ) : (
                    <div className="conditions-rules-list">
                        {rules.map((rule, idx) => renderRule(rule, idx))}

                        <button className="condition-add-rule-btn reset-btn-by-default" type="button" onClick={addRule}>
                            <img
                                style={{
                                    filter: 'brightness(0) saturate(100%) invert(34%) sepia(13%) saturate(401%) hue-rotate(182deg) brightness(90%) contrast(86%)',
                                }}
                                src={Images.addBluePlus}
                                alt="add"
                            />
                            <p> Add another rule</p>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
